import { useMemo, useRef, type KeyboardEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { getDictionary, type Locale } from "../../i18n/dictionary";
import { useOutletContext } from "react-router-dom";
import { useLinkedChildren } from "./useLinkedChildren";
import { LoadingChildren, ChildrenLoadError } from "./LoadState";
import { SyncNote } from "./SyncNote";
import { buildOnePager } from "../../progress/onePager";
import { localeTag } from "../../progress/text";
import { gameName } from "./gameName";
import { ChildMessages } from "./ChildMessages";
import { OnePagerView, SectionTitle } from "./OnePagerView";
import { TimelineView } from "./TimelineView";
import { ChildAdults, MyLinkDetails } from "./LinkDetails";

function formatDate(locale: Locale, iso: string): string {
  return new Date(iso).toLocaleDateString(localeTag(locale), { day: "numeric", month: "short" });
}

export { gameName } from "./gameName";

// ---- Back button -------------------------------------------------------------

function BackButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: "transparent",
        border: 0,
        color: "var(--ink-soft)",
        fontFamily: "var(--font-body)",
        fontWeight: 600,
        fontSize: 14,
        cursor: "pointer",
        padding: "6px 0",
        marginBottom: 20,
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="zchev"
        style={{ transform: "scaleX(-1)" }}
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
      {label}
    </button>
  );
}

// ---- Session row -------------------------------------------------------------

function SessionRow({
  gameId,
  durationSeconds,
  at,
  locale,
}: {
  gameId: string;
  durationSeconds: number;
  at: string;
  locale: Locale;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "11px 20px",
        borderBottom: "1px solid var(--line)",
        gap: 12,
      }}
    >
      <span style={{ fontSize: 15, color: "var(--ink)", flex: 1, minWidth: 0 }}>
        {gameName(gameId, locale)}
      </span>
      <div
        style={{
          display: "flex",
          gap: 14,
          flex: "none",
          color: "var(--ink-faint)",
          fontSize: 13.5,
        }}
      >
        <span>{Math.max(1, Math.round(durationSeconds / 60))} min</span>
        <span>{formatDate(locale, at)}</span>
      </div>
    </div>
  );
}

// ---- Pending / revoked state -------------------------------------------------

function BlockedState({
  title,
  body,
  tint,
}: {
  title: string;
  body: string;
  tint: "peach" | "sunk";
}) {
  return (
    <div
      style={{
        background:
          tint === "peach" ? "var(--peach-tint)" : "var(--surface-sunk)",
        border: `1px solid ${tint === "peach" ? "var(--peach)" : "var(--line-strong)"}`,
        borderRadius: "var(--r-lg)",
        padding: "24px 22px",
        marginTop: 8,
      }}
    >
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 18,
          color:
            tint === "peach" ? "var(--peach-deep)" : "var(--ink-faint)",
          margin: "0 0 10px",
        }}
      >
        {title}
      </h2>
      <p
        style={{
          color:
            tint === "peach" ? "var(--peach-deep)" : "var(--ink-faint)",
          fontSize: 15.5,
          lineHeight: 1.6,
          margin: 0,
        }}
      >
        {body}
      </p>
    </div>
  );
}

// ---- Tabs: Overview | Messages ----------------------------------------------
//
// WAI-ARIA tabs with automatic activation: arrow keys move between tabs (and
// swap left/right under a right-to-left layout), Home/End jump to the ends, and
// only the selected tab is in the tab order. The choice lives in ?tab= so the
// Messages tab can be linked to and survives a reload.

type ChildTab = "overview" | "messages";
const CHILD_TABS: readonly ChildTab[] = ["overview", "messages"];

function ChildTabs({
  value,
  onChange,
  labels,
  label,
}: {
  value: ChildTab;
  onChange: (t: ChildTab) => void;
  labels: Record<ChildTab, string>;
  label: string;
}) {
  const refs = useRef<Record<ChildTab, HTMLButtonElement | null>>({ overview: null, messages: null });

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const i = CHILD_TABS.indexOf(value);
    const rtl = getComputedStyle(e.currentTarget).direction === "rtl";
    let next: number | null = null;
    if (e.key === "ArrowRight") next = rtl ? i - 1 : i + 1;
    else if (e.key === "ArrowLeft") next = rtl ? i + 1 : i - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = CHILD_TABS.length - 1;
    if (next === null) return;
    e.preventDefault();
    const tab = CHILD_TABS[(next + CHILD_TABS.length) % CHILD_TABS.length];
    onChange(tab);
    refs.current[tab]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--line)", marginBottom: 20, flexWrap: "wrap" }}
    >
      {CHILD_TABS.map((k) => {
        const on = value === k;
        return (
          <button
            key={k}
            ref={(el) => {
              refs.current[k] = el;
            }}
            type="button"
            role="tab"
            id={`child-tab-${k}`}
            aria-selected={on}
            aria-controls={`child-panel-${k}`}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(k)}
            style={{
              minHeight: 48,
              padding: "0 18px",
              border: 0,
              borderBottom: `3px solid ${on ? "var(--brand)" : "transparent"}`,
              marginBottom: -1,
              background: "transparent",
              color: on ? "var(--brand)" : "var(--ink-soft)",
              fontFamily: "var(--font-body)",
              fontWeight: on ? 700 : 600,
              fontSize: 15,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {labels[k]}
          </button>
        );
      })}
    </div>
  );
}

// ---- ChildDetail screen ------------------------------------------------------


export function ChildDetail() {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const navigate = useNavigate();
  const { childId } = useParams<{ childId: string }>();
  const t = dict.service.childDetail;
  const tc = dict.service.children;
  const [params, setParams] = useSearchParams();
  const tab: ChildTab = params.get("tab") === "messages" ? "messages" : "overview";
  const setTab = (next: ChildTab) => {
    const p = new URLSearchParams(params);
    if (next === "overview") p.delete("tab");
    else p.set("tab", next);
    setParams(p, { replace: true });
  };

  const outlet = useOutletContext<{ userId?: string } | null>();
  const { linked, loading, error, refresh } = useLinkedChildren(outlet?.userId);
  const lc = childId ? linked.find((x) => x.child.id === childId) : undefined;

  const now = useMemo(() => new Date(), []);
  const onePager = useMemo(
    () =>
      lc
        ? buildOnePager({
            stepStates: lc.stepStates,
            sessions: lc.sessions,
            supportProfile: lc.supportProfile,
            homeNotes: lc.homeNotes,
            now,
            locale,
          })
        : null,
    [lc, now, locale],
  );

  // Same ordering rule as Home: never claim "this child is not linked to your
  // account" while we are still fetching, or because the fetch failed.
  if (loading) return <LoadingChildren label={t.loading} />;
  if (error) return <ChildrenLoadError message={error} onRetry={refresh} />;

  if (!lc) {
    return (
      <div>
        <BackButton
          label={t.backToChildren}
          onClick={() => navigate("/children")}
        />
        <p style={{ color: "var(--ink-soft)", fontSize: 15.5 }}>
          {t.notLinked}
        </p>
      </div>
    );
  }

  const { child, link, linkState, ownerEmail, sessions } = lc;
  const recentSessions = sessions.slice(0, 5);
  const canSeeProgress = link.relation === "owner" || link.scope.includes("progress");
  const canSeeResults = link.relation === "owner" || link.scope.includes("results");

  return (
    <div>
      <BackButton
        label={t.backToChildren}
        onClick={() => navigate("/children")}
      />

      {/* Child header */}
      <div
        style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background:
              linkState === "revoked" ? "var(--surface-sunk)" : "var(--brand-tint)",
            display: "grid",
            placeItems: "center",
            flex: "none",
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 22,
            color: linkState === "revoked" ? "var(--ink-faint)" : "var(--brand)",
          }}
        >
          {child.displayLabel.charAt(0)}
        </span>
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: 26,
              color:
                linkState === "revoked" ? "var(--ink-faint)" : "var(--ink-deep)",
              margin: 0,
            }}
          >
            {child.displayLabel}
          </h1>
          <span
            style={{ fontSize: 13, color: "var(--ink-faint)", marginTop: 3, display: "block" }}
          >
            {link.relation === "owner"
              ? tc.ownerTag
              : ownerEmail
                ? `${tc.viewerTag} ${ownerEmail}`
                : tc.viewerTag}
          </span>
        </div>
      </div>

      {/* The adult's own side of the link: a viewer sees their role and sets
          the label the family knows them by; the owner sees everyone they
          share this child with. Adult labels only — never about the child. */}
      {link.relation === "viewer" ? (
        <MyLinkDetails key={child.id} childId={child.id} accountId={link.accountId} role={link.role ?? null} label={link.label ?? null} />
      ) : (
        <ChildAdults key={child.id} childId={child.id} />
      )}

      {linkState === "pending" && (
        <BlockedState title={t.pendingTitle} body={t.pendingBody} tint="peach" />
      )}

      {linkState === "revoked" && (
        <BlockedState title={t.revokedTitle} body={t.revokedBody} tint="sunk" />
      )}

      {/* Active: two tabs. Overview is the One-Pager, Timeline and recent
          play; Messages is the history of practice ideas sent for this child. */}
      {linkState === "active" && (
        <ChildTabs
          value={tab}
          onChange={setTab}
          label={t.tabsLabel}
          labels={{ overview: t.tabOverview, messages: t.tabMessages }}
        />
      )}

      {linkState === "active" && tab === "messages" && (
        <div role="tabpanel" id="child-panel-messages" aria-labelledby="child-tab-messages" tabIndex={0}>
          <ChildMessages key={child.id} childId={child.id} canSee={canSeeResults} isOwner={link.relation === "owner"} locale={locale} dict={dict} />
        </div>
      )}

      {linkState === "active" && tab === "overview" && (
        <div
          role="tabpanel"
          id="child-panel-overview"
          aria-labelledby="child-tab-overview"
          tabIndex={0}
          style={{ display: "flex", flexDirection: "column", gap: 28 }}
        >
          {canSeeProgress && onePager && (
            <>
              <OnePagerView model={onePager} locale={locale} dict={dict} />
              {onePager.hasSteps ? (
                <TimelineView stepStates={lc.stepStates} locale={locale} dict={dict} now={now} />
              ) : (
                <p
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--line)",
                    borderRadius: "var(--r-lg)",
                    padding: "16px 20px",
                    color: "var(--ink-soft)",
                    fontSize: 15,
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {t.noStepsYet}
                </p>
              )}
            </>
          )}

          <section>
            <SectionTitle>{t.recentPlay}</SectionTitle>
            {recentSessions.length > 0 ? (
              <div
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-lg)",
                  overflow: "hidden",
                }}
              >
                {recentSessions.map((s, i) => (
                  <SessionRow
                    key={`${s.gameId}-${s.at}-${i}`}
                    gameId={s.gameId}
                    durationSeconds={s.durationSeconds}
                    at={s.at}
                    locale={locale}
                  />
                ))}
              </div>
            ) : (
              <p
                style={{
                  color: "var(--ink-soft)",
                  fontSize: 15.5,
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-lg)",
                  padding: "16px 20px",
                  margin: 0,
                }}
              >
                {t.noSessions}
              </p>
            )}
          </section>

          <p style={{ fontSize: 13, color: "var(--ink-faint)", margin: 0 }}>
            {t.footer}
          </p>
          <SyncNote at={recentSessions[0]?.at} />
        </div>
      )}
    </div>
  );
}
